import { useExternalScript } from "src/hooks/useExternalScript";
import { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { buildQueryString } from "src/helper/query-string";
import {
  PATH,
  ArgStatusType,
  ARG_STATUS,
  ERROR_TEXT,
  PLAYING_LIMIT_CAMERA_NUM,
  SAFIE_STORAGE_KEYS,
} from "src/const";

const parser = new DOMParser();
const stringSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
        <g clip-path="url(#clip0_11548_2476)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M4 7.99967C3.64638 7.99967 3.30724 8.14015 3.05719 8.3902C2.80714 8.64025 2.66667 8.97939 2.66667 9.33301V22.6663C2.66667 23.02 2.80714 23.3591 3.05719 23.6091C3.30724 23.8592 3.64638 23.9997 4 23.9997H18.6667C19.0203 23.9997 19.3594 23.8592 19.6095 23.6091C19.8595 23.3591 20 23.02 20 22.6663V21.333C20 20.5966 20.597 19.9997 21.3333 19.9997C22.0697 19.9997 22.6667 20.5966 22.6667 21.333V22.6663C22.6667 23.7272 22.2452 24.7446 21.4951 25.4948C20.7449 26.2449 19.7275 26.6663 18.6667 26.6663H4C2.93913 26.6663 1.92172 26.2449 1.17157 25.4948C0.421428 24.7446 0 23.7272 0 22.6663V9.33301C0 8.27214 0.421427 7.25473 1.17157 6.50458C1.92172 5.75444 2.93913 5.33301 4 5.33301H6.66667C7.40305 5.33301 8 5.92996 8 6.66634C8 7.40272 7.40305 7.99967 6.66667 7.99967H4ZM12.88 6.66634C12.88 5.92996 13.477 5.33301 14.2133 5.33301H18.6667C19.7275 5.33301 20.7449 5.75444 21.4951 6.50458C22.2452 7.25473 22.6667 8.27214 22.6667 9.33301V13.2341L22.806 13.3733L29.8852 8.25267C30.2911 7.95912 30.8272 7.91777 31.2732 8.14564C31.7193 8.3735 32 8.83213 32 9.33301V22.6663C32 23.4027 31.403 23.9997 30.6667 23.9997C29.9303 23.9997 29.3333 23.4027 29.3333 22.6663V11.943L23.4481 16.2C22.9176 16.5838 22.1869 16.5255 21.7239 16.0625L20.3905 14.7292C20.1405 14.4791 20 14.14 20 13.7863V9.33301C20 8.97939 19.8595 8.64025 19.6095 8.3902C19.3594 8.14015 19.0203 7.99967 18.6667 7.99967H14.2133C13.477 7.99967 12.88 7.40272 12.88 6.66634Z" fill="white"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.390524 0.390524C0.911223 -0.130175 1.75544 -0.130175 2.27614 0.390524L31.6095 29.7239C32.1302 30.2446 32.1302 31.0888 31.6095 31.6095C31.0888 32.1302 30.2446 32.1302 29.7239 31.6095L0.390524 2.27614C-0.130175 1.75544 -0.130175 0.911223 0.390524 0.390524Z" fill="white"/>
        </g>
        <defs>
        <clipPath id="clip0_11548_2476">
        <rect width="32" height="32" fill="white"/>
        </clipPath>
        </defs>
        </svg>`;
const NAME_NODE_HEIGHT = 36;

export const useCameraDevices = (
  videoRef: React.RefObject<HTMLDivElement> | null,
  shopName: string | null,
  safieApiKey: { status: ArgStatusType; data: string | undefined }
) => {
  const history = useHistory();
  const location = useLocation();
  const isViewOnFrame = window !== window.parent;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const [playerList, setPlayerList] = useState<
    { player: any; node: HTMLDivElement; deviceId: string }[]
  >([]);
  const [devices, setDevices] = useState<
    {
      deviceId: string;
      setting: { name: string };
      status: { videoStreaming: boolean };
    }[]
  >([]);
  const [width, setWidth] = useState(0);
  const [cameraHeight, setCameraHeight] = useState(0);
  const isScriptLoaded = useExternalScript(
    "https://safie.link/sdk/js/api/v1/latest/"
  );
  const win: any = window;
  const [playingVideo, setPlayingVideo] = useState(0);
  const [isSafieReady, setIsSafieReady] = useState(false);

  const playerRef = useRef(null);
  const deviceSelectRef = useRef(null);

  const selectCamera = useCallback((deviceId: string, device: any) => {
    const params = new URLSearchParams();

    params.append("device_id", deviceId);
    // NOTE: APIキーがASSIGNステータス（手動指定）の場合のみクエリパラメータとして渡す
    if (safieApiKey.status === ARG_STATUS.ASSIGN && safieApiKey.data) {
      params.append("api-key", safieApiKey.data);
    }
    // デバイス情報をstateで渡す（queryDevices()の呼び出しを回避）
    history.push(buildQueryString(PATH.STREAMING, params), { device });
  }, [safieApiKey, history]);

  const getUserInfo = useCallback(async () => {
    try {
      await win.Safie.Users.queryInformation();
    } catch (error) {
      console.error("userInfo error", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // windowは不変のため依存配列は空でOK

  const initialize = useCallback(async () => {
    // tokenの設定とユーザ情報の取得
    try {
      const { tokenType } = await win.Safie.Auth.initialize();
      // content.style.display = '';
      if (tokenType === win.Safie.Auth.TokenType.AccessToken) {
        await getUserInfo();
      }
    } catch (error) {
      console.error("initialize error", error);
      setErrorText(ERROR_TEXT.INITIALIZE_ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUserInfo]); // windowは不変のため依存配列から除外

  const getDevices = useCallback(async () => {
    try {
      const devices = await win.Safie.Devices.queryDevices({ limit: 100 });
      if (devices.list.length === 0) {
        // カメラが一つもなければ、エラーを返す
        setErrorText(ERROR_TEXT.DISCOLLABORATE_SAFIE_CAMERA);
        setIsLoading(false);
        return;
      }
      if (shopName) {
        // _前で一致
        const shopNameStrList = shopName.split("_");
        const partOfShopName = shopNameStrList[0];

        // 全文一致
        const shopDeviceList = devices.list.filter(
          (device: {
            deviceId: string;
            setting: { name: string };
            status: { videoStreaming: boolean };
          }) =>
            device.setting.name.includes(shopName) ||
            device.setting.name.includes(partOfShopName)
        );

        if (shopDeviceList.length === 0) {
          // filter後のカメラが一つもなければ、エラーを返す
          setErrorText(ERROR_TEXT.FILTER_SAFIE_CAMERA_IS_NONE);
          setIsLoading(false);
          return;
        }
        setDevices(shopDeviceList);
      } else {
        setDevices(devices.list);
      }
    } catch (error) {
      console.error("getDevices error", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopName]); // windowは不変のため依存配列から除外

  const setToken = useCallback(async () => {
    // グローバルフラグで二重setTokenを防ぐ（同一ページ内での二重実行を回避）
    // window.safieTokenSet はページリロード時にクリアされるため、再訪時も正常に動作
    const tokenAlreadySetInPage = window.safieTokenSet;

    const tokenAlreadySetInSession = isViewOnFrame ? null : sessionStorage.getItem(SAFIE_STORAGE_KEYS.TOKEN_SET);

    // 既にトークンが設定済みの場合はスキップ
    if (tokenAlreadySetInPage || tokenAlreadySetInSession) {
      return true;
    }

    // Safie SDKは setToken() で既存トークンを上書きできる
    // removeToken() を呼ぶとSDK内部状態が壊れるため呼ばない

    if (
      safieApiKey.status !== ARG_STATUS.SUCCESS &&
      safieApiKey.status !== ARG_STATUS.ASSIGN
    ) {
      return false;
    }
    const token = safieApiKey.data;
    if (token == null || token === "") {
      return false;
    }

    // Safie SDKが利用可能かチェック
    if (!win.Safie || !win.Safie.Auth || !win.Safie.Auth.setToken) {
      console.error("[view_device] Safie SDK not available");
      return false;
    }

    try {
      const setTokenPromise = win.Safie.Auth.setToken(token, "apiKey");

      await Promise.race([
        setTokenPromise,
        new Promise((_, reject) =>
          setTimeout(() => {
            reject(new Error('setToken timeout after 10 seconds'));
          }, 10000)
        )
      ]);

      // グローバルフラグとsessionStorageフラグを設定
      window.safieTokenSet = true;
      if (!isViewOnFrame) {
        sessionStorage.setItem(SAFIE_STORAGE_KEYS.TOKEN_SET, 'true');
      }
      return true;
    } catch (error: any) {
      console.error("[view_device] setToken error:", error?.message);

      // タイムアウトエラーの場合、API リクエストは成功している可能性が高い
      if (error?.message?.includes('timeout')) {
        console.warn("[view_device] Timeout, treating as potential success");
        window.safieTokenSet = true;
        if (!isViewOnFrame) {
          sessionStorage.setItem(SAFIE_STORAGE_KEYS.TOKEN_SET, 'true');
        }
        await new Promise(resolve => setTimeout(resolve, 500));
        return true;
      }

      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safieApiKey]); // windowは不変のため依存配列から除外

  useEffect(() => {
    if (playerList.length === 1 && playerList[0].player) {
      // ビデオが一つ かつ 再生中の場合自動遷移する
      const deviceId = playerList[0].deviceId;
      const device = devices.find(d => d.deviceId === deviceId);
      selectCamera(deviceId, device);
    }
  }, [playerList, selectCamera, devices]);

  const removeToken = async () => {
    await win.Safie.Auth.removeToken();
  };

  const addPlayer = useCallback(async () => {
    let player: any = null;
    try {
      if (devices.length === 0) {
        return;
      }
      if (playerList.length > 0) {
        playerList.forEach((playerItem) => {
          if (playerItem.player) {
            // NOTE: CameraDevices画面ではイベントリスナーを登録していないため、dispose()のみで十分
            // Streaming画面ではplayer.on()でイベントリスナーを登録しているため、dispose()前にoff()で解除が必要
            try {
              playerItem.player.dispose();
            } catch (error) {
              console.error('Failed to dispose player:', error);
            }
          }
          // 2. DOMノードの削除
          try {
            playerItem.node.remove();
          } catch (error) {
            console.error('Failed to remove video node:', error);
          }
        });
        setPlayerList([]);
      }
      // 再生中videoの数
      let tmpPlayingVideo = 0;
      let cameraWidth = 280;
      if (width) {
        const blocks = devices.length < 3 ? devices.length : 3;
        cameraWidth = width / blocks - 20;
      }
      let cameraHeight = (cameraWidth * 9) / 16 + NAME_NODE_HEIGHT;
      setCameraHeight(cameraHeight);
      devices.map(async (device, index: number) => {
        const isStreaming: boolean = device.status.videoStreaming;
        // 店舗カメラノード(divNode)
        const divNode = document.createElement("div");
        divNode.style.margin = "8px";
        divNode.style.cursor = "pointer";
        divNode.style.display = "block";
        divNode.style.width = `${cameraWidth}px`;
        divNode.style.height = `${cameraHeight}px`;
        divNode.style.borderRadius = "4px";
        divNode.style.overflow = "hidden"; // 子要素によりborder-radiusが効かないので設定

        if (isStreaming) {
          divNode.addEventListener("click", () =>
            selectCamera(device.deviceId, device)
          );
        }

        // 動画ノード
        const videoNode = document.createElement("div");
        videoNode.style.width = `${cameraWidth}px`;
        videoNode.style.height = `${(cameraWidth * 9) / 16}px`;
        videoNode.style.aspectRatio = "16/9";
        videoNode.style.pointerEvents = "none";

        // 非接続ノード
        const disconnectNode = document.createElement("div");
        disconnectNode.style.width = `${cameraWidth}px`;
        disconnectNode.style.height = `${(cameraWidth * 9) / 16}px`;
        disconnectNode.style.aspectRatio = "16/9";
        disconnectNode.style.background = "#37352F";
        disconnectNode.style.pointerEvents = "none";
        disconnectNode.style.textAlign = "center";
        disconnectNode.style.display = "flex";
        disconnectNode.style.flexDirection = "column";
        disconnectNode.style.alignItems = "center";
        disconnectNode.style.justifyContent = "center";
        disconnectNode.style.color = "#FFFFFF";

        /** 非接続ノードの説明文の要素 */
        const descriptionElement = document.createElement("div");
        descriptionElement.textContent = "未接続";
        descriptionElement.style.marginTop = "8px";
        descriptionElement.style.fontSize = "14px";
        descriptionElement.style.fontWeight = "500";

        /** 非接続ノード用のアイコン */
        const svgIcon = parser.parseFromString(
          stringSvg,
          "image/svg+xml"
        ).documentElement;

        disconnectNode.appendChild(svgIcon);
        disconnectNode.appendChild(descriptionElement);

        // 非再生ノード
        const notPlayNode = document.createElement("div");
        notPlayNode.style.width = `${cameraWidth}px`;
        notPlayNode.style.height = `${(cameraWidth * 9) / 16}px`;
        notPlayNode.style.aspectRatio = "16/9";
        notPlayNode.style.background = "#37352F";
        notPlayNode.style.color = "#FFF";
        notPlayNode.style.pointerEvents = "none";
        notPlayNode.style.textAlign = "center";
        notPlayNode.style.display = "flex";
        notPlayNode.style.flexDirection = "column";
        notPlayNode.style.justifyContent = "center";
        notPlayNode.style.alignItems = "center";

        const notPlayNodeDescription = descriptionElement.cloneNode(true);
        notPlayNodeDescription.textContent = "制限により再生できません";

        const notPlayNodeSvgIcon = svgIcon.cloneNode(true);

        notPlayNode.appendChild(notPlayNodeSvgIcon);
        notPlayNode.appendChild(notPlayNodeDescription);

        // 店舗名ノード
        const nameNode = document.createElement("div");
        nameNode.style.width = `${cameraWidth}px`;
        nameNode.style.height = `${NAME_NODE_HEIGHT}px`;
        nameNode.style.background = "#FFFFFF";
        nameNode.style.padding = "8px";
        nameNode.style.fontWeight = "500";
        nameNode.style.fontSize = "14px";
        nameNode.textContent = device.setting.name;
        nameNode.style.overflow = "hidden";
        nameNode.style.textOverflow = "ellipsis";
        nameNode.style.whiteSpace = "nowrap";

        if (videoRef && videoRef.current) {
          videoRef.current.append(divNode);
        }
        if (isStreaming && PLAYING_LIMIT_CAMERA_NUM >= index + 1) {
          divNode.append(videoNode);
          divNode.append(nameNode);
          player = new win.Safie.Player.StreamingPlayer(videoNode);
          setPlayerList((prev) => [
            ...prev,
            { player: player, node: divNode, deviceId: device.deviceId },
          ]);
          player.deviceId = device.deviceId;
          player.muted = true;

          if (device.status.videoStreaming) {
            player.userInteractions = false;
            player.play();
            tmpPlayingVideo += 1;
            setPlayingVideo(tmpPlayingVideo);
          }
        } else if (isStreaming && PLAYING_LIMIT_CAMERA_NUM < index + 1) {
          setPlayerList((prev) => [
            ...prev,
            { player: null, node: divNode, deviceId: device.deviceId },
          ]);
          divNode.append(notPlayNode);
          divNode.append(nameNode);
        } else {
          setPlayerList((prev) => [
            ...prev,
            { player: null, node: divNode, deviceId: device.deviceId },
          ]);
          divNode.append(disconnectNode);
          divNode.append(nameNode);
        }
      });
    } catch (error: any) {
      alert(`エラーtype: ${error.type} message: ${error.message}`);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, playerList, width, videoRef, selectCamera]); // windowは不変のため依存配列から除外

  // Safie SDKの準備状態を監視
  useEffect(() => {
    if (!isScriptLoaded) return;

    // Safie SDKが既に利用可能な場合
    if (win.Safie && win.Safie.Auth) {
      setIsSafieReady(true);
      return;
    }

    // Safie SDKが利用可能になるまでポーリング
    const checkInterval = setInterval(() => {
      if (win.Safie && win.Safie.Auth) {
        setIsSafieReady(true);
        clearInterval(checkInterval);
      }
    }, 100);

    const timeout = setTimeout(() => {
      console.error("[view_device] Safie SDK not available after 10s");
      clearInterval(checkInterval);
    }, 10000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScriptLoaded]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasExecutedRef = useRef(false);

  useEffect(() => {
    // iframe環境ではsessionStorageフラグを使わない（親ウィンドウ間で遷移した際にフラグが残り続ける問題を回避）
    const hasExecutedInSession = isViewOnFrame ? null : sessionStorage.getItem(SAFIE_STORAGE_KEYS.DEVICES_INITIALIZED);

    if (!isScriptLoaded) return;
    if (!isSafieReady) return;
    if (!safieApiKey) return;
    if (safieApiKey.status !== ARG_STATUS.SUCCESS && safieApiKey.status !== ARG_STATUS.ASSIGN) return;
    if (devices.length > 0) return;
    if (hasExecutedRef.current || hasExecutedInSession) return;

    hasExecutedRef.current = true;
    // iframe環境ではsessionStorageフラグを設定しない
    if (!isViewOnFrame) {
      sessionStorage.setItem(SAFIE_STORAGE_KEYS.DEVICES_INITIALIZED, 'true');
    }

    (async () => {
      const MAX_RETRIES = 3;
      let retryCount = 0;

      // リトライロジック付きでsetTokenを実行
      while (retryCount < MAX_RETRIES) {
        try {
          const result = await setToken();

          if (!result) {
            retryCount++;
            if (retryCount < MAX_RETRIES) {
              await new Promise(resolve => setTimeout(resolve, 2000));
              continue;
            } else {
              console.error("[view_device] setToken failed after max retries");
              hasExecutedRef.current = false;
              if (!isViewOnFrame) {
                sessionStorage.removeItem(SAFIE_STORAGE_KEYS.DEVICES_INITIALIZED);
              }
              setErrorText(ERROR_TEXT.API_KEY_ERROR);
              setIsLoading(false);
              return;
            }
          }

          // setToken成功（ローディングはgetDevices完了後にfalseになる）
          setErrorText(undefined);
          break;
        } catch (error: any) {
          console.error("[view_device] setToken error:", error);
          retryCount++;
          if (retryCount < MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            continue;
          } else {
            hasExecutedRef.current = false;
            if (!isViewOnFrame) {
              sessionStorage.removeItem(SAFIE_STORAGE_KEYS.DEVICES_INITIALIZED);
            }
            setErrorText(ERROR_TEXT.API_KEY_ERROR);
            setIsLoading(false);
            return;
          }
        }
      }

      try {
        // グローバルフラグで二重初期化を防ぐ（同一ページ内での二重実行を回避）
        // window.safieInitialized はページリロード時にクリアされるため、再訪時も正常に動作
        if (!window.safieInitialized) {
          await initialize();
          window.safieInitialized = true;
          // Safie SDK初期化完了フラグを設定（Streaming画面での二重初期化を防ぐ）
          // iframe環境ではsessionStorageフラグを設定しない
          if (!isViewOnFrame) {
            sessionStorage.setItem(SAFIE_STORAGE_KEYS.INITIALIZED, 'true');
          }
        }

        await getDevices();
      } catch (error: any) {
        console.error("[view_device] Initialization error:", error);
        hasExecutedRef.current = false;
        if (!isViewOnFrame) {
          sessionStorage.removeItem(SAFIE_STORAGE_KEYS.DEVICES_INITIALIZED);
        }
        if (error?.type === "forbidden") {
          setErrorText(ERROR_TEXT.IP_RESTRICTION_ERROR);
          setIsLoading(false);
        }
        return;
      }
    })();

    return () => {
      const timeout = timeoutRef.current;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScriptLoaded, isSafieReady, safieApiKey.status, safieApiKey.data, location.key]); // statusとdataを個別に監視、location.keyでページ遷移を検知

  useEffect(() => {
    if (devices.length === 0) return;
    setIsLoading(false);
    addPlayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, width]); // 関数は依存配列から除外（無限ループ防止のため）

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });
    if (videoRef && videoRef.current) {
      resizeObserver.observe(videoRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [videoRef]);

  return {
    isScriptLoaded,
    playerRef,
    deviceSelectRef,
    addPlayer,
    setToken,
    removeToken,
    getDevices,
    devices,
    isLoading,
    errorText,
    playingVideo,
    width,
    cameraHeight,
  };
};
